import { FieldType } from '@databases/model/field';
import {
  type HyperlinkValue,
  type MutableHyperlinkValue,
  type MutableSingleHyperlinkValue,
  type SingleHyperlinkValue,
  type ValueJSON
} from '@databases/model/value';
import { type Op } from 'quill-delta';
import * as Y from 'yjs';

import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from '../helpers/types';
import { ValueProperties } from '../y-entry';
import { YFieldType } from '../y-field';

const HyperlinkValueProperties = {
  url: 'u',
  id: 'i'
};

class YSingleHyperlinkValue
  extends SynchronizedRecord
  implements SingleHyperlinkValue, MutableSingleHyperlinkValue
{
  static mapper(value: unknown): YSingleHyperlinkValue | undefined {
    if (value instanceof Y.Map) {
      return new YSingleHyperlinkValue(value);
    }
  }

  get url(): string {
    return this.getText(HyperlinkValueProperties.url)?.toJSON() || '';
  }

  get id(): string {
    return this.getString(HyperlinkValueProperties.id, '');
  }

  private initializeUrl() {
    if (!this.getText(HyperlinkValueProperties.url)) {
      this.set(HyperlinkValueProperties.url, new Y.Text());
    }
  }

  applyUrlDelta(delta: Op[]): void {
    this.initializeUrl();
    this.getText(HyperlinkValueProperties.url)!.applyDelta(delta);
  }

  clone(): YSingleHyperlinkValue {
    return YSingleHyperlinkValue.fromJson(this.toJSON());
  }

  toJSON(): SingleHyperlinkValue {
    return {
      url: this.url?.toString() ?? '',
      id: this.id
    };
  }

  static fromJson(value: SingleHyperlinkValue): YSingleHyperlinkValue {
    return new YSingleHyperlinkValue(
      new Y.Map([
        [HyperlinkValueProperties.url, new Y.Text(value.url)],
        [HyperlinkValueProperties.id, value.id]
      ])
    );
  }
}

export class YHyperlinkValue
  implements HyperlinkValue, MutableHyperlinkValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSingleHyperlinkValue>>;

  constructor(private input: Y.Map<unknown>) {
    this.valueElement = this.buildValueElement();
  }

  private buildValueElement(): SynchronizedMapElement<
    SynchronizedArray<YSingleHyperlinkValue>
  > {
    return new SynchronizedMapElement<SynchronizedArray<YSingleHyperlinkValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(
            yarray as Y.Array<unknown>,
            YSingleHyperlinkValue.mapper
          );
        }
      },
      () => new Y.Array()
    );
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  get type(): FieldType.Hyperlink {
    return FieldType.Hyperlink;
  }

  add(value: SingleHyperlinkValue): void {
    this.valueElement.valueRequired.push([YSingleHyperlinkValue.fromJson(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YSingleHyperlinkValue.fromJson(val)
    ]);
  }

  setTo(value: SingleHyperlinkValue[]): void {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.buildValueElement();

    this.valueElement.valueRequired.push(
      value.map((v) => YSingleHyperlinkValue.fromJson(v))
    );
  }

  toJSON(): ValueJSON<FieldType.Hyperlink, SingleHyperlinkValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(json: ValueJSON<FieldType.Hyperlink, SingleHyperlinkValue[]>) {
    return new YHyperlinkValue(
      new Y.Map([
        [ValueProperties.type, YFieldType.HYPERLINK],
        [
          ValueProperties.value,
          Y.Array.from(
            json.value.map(
              (val) =>
                new Y.Map([
                  [HyperlinkValueProperties.url, new Y.Text(val.url)],
                  [HyperlinkValueProperties.id, val.id]
                ])
            )
          )
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
